import { type Message as TMessage, useStores } from '@littlebird/sdk';
import { hashSendContext } from '@littlebird/ui-utils';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useLayoutEffect, useState } from 'react';

import { AppLogo } from '@/assets/icons/AppLogo';
import { List } from '@/components/blocks/Message/MessageList/MessageList';
import { chatFindTunnel } from '@/components/blocks/Message/MessageList/useChatFind';
import { IncludeSelectionButton } from '@/components/blocks/Thread/Chat';
import { Composer } from '@/components/blocks/Thread/Composer';
import { YStack } from '@/components/ui';
import { ChatContextProvider } from '@/features/chat/chat-context';
import { useMarkFeedChatSeenWhileVisible } from '@/features/feed/use-mark-feed-chat-seen';
import { HivemindBanner } from '@/features/hivemind';
import { cn } from '@/lib/utils';
import { uiStore } from '@/stores/ui.store';

import { ContextChatBanner } from './context-chat-banner';
import { RecentGeneralChatsMenu } from './context-chat-recent-chats';
import {
  useSessionSendContextBuilder,
  useSessionTitle,
} from './context-chat.hooks';
import { useContextChatHost } from './context-chat.host';
import { contextChatStore } from './context-chat.state';

import type { ContextChatSession } from './context-chat.types';

const setIsDraggingFile = (value: boolean) => uiStore.setIsDraggingFile(value);

export const ChatPanelContent = observer(function ChatPanelContent({
  emptyText,
}: {
  // What the chat says before its first message, in place of the general prompt
  emptyText?: string;
} = {}) {
  const session = contextChatStore.activeSession;

  if (!session) return null;

  const { mode } = contextChatStore;
  const stripped = mode === 'collapsed';
  const showBody = mode === 'floating' || mode === 'docked';

  return (
    <PanelShell
      session={session}
      stripped={stripped}
      showBody={showBody}
      emptyText={emptyText}
    />
  );
});

const PanelShell = observer(function PanelShell({
  session,
  stripped,
  showBody,
  emptyText,
}: {
  session: ContextChatSession;
  stripped: boolean;
  showBody: boolean;
  emptyText?: string;
}) {
  return (
    <YStack className={cn('relative h-full w-full', showBody && 'pb-4 gap-3')}>
      {showBody && <chatFindTunnel.Out />}
      <ActiveSessionBody
        session={session}
        stripped={stripped}
        showBody={showBody}
        emptyText={emptyText}
      />
    </YStack>
  );
});

const ActiveSessionBody = observer(function ActiveSessionBody({
  session,
  stripped,
  showBody,
  emptyText,
}: {
  session: ContextChatSession;
  stripped: boolean;
  showBody: boolean;
  emptyText?: string;
}) {
  const { messages, threads } = useStores();
  const host = useContextChatHost();
  const [selectionContainer, setSelectionContainer] =
    useState<HTMLDivElement | null>(null);
  const baseGetSendContext = useSessionSendContextBuilder(session.context);

  // Skips the context payload on follow-up turns when it hasn't
  // changed since our last send. The hash is only treated as "sent"
  // once an assistant reply lands in the thread (see effect below) so
  // a failed send doesn't suppress context on retry. Pending state
  // lives on the session itself so a user can switch meetings between
  // sending and the reply landing without losing or cross-committing
  // hashes across sessions.
  const sessionId = session.id;
  const getSendContext = useCallback(
    (sendThreadId?: string) => {
      const content = baseGetSendContext?.();

      if (!content) return undefined;

      const hash = hashSendContext(content);

      if (hash === session.lastSentContextHash) return undefined;

      const baseline =
        threads.getById(sendThreadId ?? session.threadId)?.lastMessageId ??
        null;

      contextChatStore.setPendingContextHash(sessionId, hash, baseline);

      return content;
    },
    [
      baseGetSendContext,
      session.lastSentContextHash,
      session.threadId,
      sessionId,
      threads,
    ]
  );

  const sessionTitle = useSessionTitle(session.context);

  useEffect(() => {
    void threads.openThread(session.threadId);
  }, [session.threadId, threads]);

  // Read inline (not via useMemo) so the MobX observer tracks message
  // store updates and re-renders when new messages arrive.
  const threadMessages = messages.getMessagesByThreadId(session.threadId);
  const thread = threads.getById(session.threadId);

  // Commit the pending send-context hash once a *new* assistant
  // message becomes the thread's `lastMessageId`. Watching
  // lastMessageId (rather than counting non-user messages) means
  // backfilling history can't trip the commit, since loading older
  // messages doesn't change the latest-message pointer.
  const lastMessageId = thread?.lastMessageId;

  useEffect(() => {
    if (!lastMessageId) return;

    const baseline = session.pendingContextBaselineLastMessageId;

    if (baseline === undefined) return;
    if (lastMessageId === baseline) return;

    const lastMsg = messages.get(lastMessageId);

    if (!lastMsg || lastMsg.role === 'user') return;

    contextChatStore.commitPendingContextHash(sessionId);
  }, [
    lastMessageId,
    session.pendingContextBaselineLastMessageId,
    sessionId,
    messages,
  ]);
  const isNewThread = !!thread && !thread.synced && !thread.lastMessageId;

  const onThreadCreated = (newThreadId: string) => {
    if (newThreadId !== session.threadId)
      contextChatStore.attachThread(session.id, newThreadId);
  };

  return (
    <div
      ref={setSelectionContainer}
      className={cn('flex flex-1 min-h-0 flex-col', !showBody && 'h-full')}
    >
      {showBody && <HivemindBanner threadId={session.threadId} inPanel />}
      {showBody && (
        <PanelMessageList
          messages={threadMessages}
          threadId={session.threadId}
          thread={thread}
          emptyText={emptyText}
        />
      )}
      {showBody && (
        <IncludeSelectionButton
          visible
          threadId={session.threadId}
          container={selectionContainer}
        />
      )}
      <ChatContextProvider
        getSendContext={getSendContext}
        chatTitle={sessionTitle}
      >
        <div
          className={cn(
            'relative isolate flex flex-col',
            showBody ? 'px-4' : 'h-full'
          )}
          onFocusCapture={
            contextChatStore.mode === 'collapsed'
              ? (e) => {
                  const element =
                    e.target instanceof HTMLElement ? e.target : null;
                  if (element?.closest('textarea, [contenteditable="true"]')) {
                    contextChatStore.expand();
                  }
                }
              : undefined
          }
        >
          {showBody && <ContextChatBanner session={session} />}
          <Composer
            autoFocus={!contextChatStore.embedded}
            threadId={session.threadId}
            setIsDraggingFile={setIsDraggingFile}
            isNewThread={isNewThread}
            onThreadCreated={onThreadCreated}
            collapsed={stripped}
            frameless={!showBody}
            dock={contextChatStore.mode !== 'docked'}
            hideVoiceInput={host.isRecording()}
            hideCostFooter={host.hideCostFooter}
            trailingAction={
              session.context.type === 'general' ? (
                <RecentGeneralChatsMenu />
              ) : undefined
            }
            placeholder={
              stripped && threadMessages.length > 0
                ? 'Continue chat'
                : 'Ask Littlebird'
            }
          />
        </div>
      </ChatContextProvider>
    </div>
  );
});

function PanelEmptyState({ text = "What's on your mind?" }: { text?: string }) {
  return (
    <YStack className="flex-1 items-center justify-center gap-2 text-foreground-muted">
      <AppLogo size={28} />
      <span className="text-sm">{text}</span>
    </YStack>
  );
}

interface PanelMessageListProps {
  messages: TMessage[];
  threadId: string;
  thread: ReturnType<ReturnType<typeof useStores>['threads']['getById']>;
  emptyText?: string;
}

const PanelMessageList = observer(function PanelMessageList({
  messages,
  threadId,
  thread,
  emptyText,
}: PanelMessageListProps) {
  useMarkFeedChatSeenWhileVisible(threadId, true);

  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const [containerHeight, setContainerHeight] = useState(0);

  // Measure synchronously before paint so VList renders with the right
  // height on first frame; otherwise its initial scroll-to-bottom and
  // ScrollRestoration both fire against the wrong viewport.
  useLayoutEffect(() => {
    if (!container) return;

    const h = container.getBoundingClientRect().height;
    setContainerHeight(h);
  }, [container, threadId]);

  useEffect(() => {
    if (!container) return;

    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const h = entry.contentRect.height;
        if (h > 0) setContainerHeight(h);
      }
    });

    ro.observe(container);

    return () => ro.disconnect();
  }, [container]);

  if (messages.length === 0) {
    // Thread has content (lastMessageId set or already synced) but
    // messages haven't streamed in yet - render blank instead of the
    // empty state so we don't flash "What's on your mind?" between
    // session switches.
    if (thread?.lastMessageId || thread?.synced) {
      return <div className="flex-1" />;
    }

    return <PanelEmptyState text={emptyText} />;
  }

  return (
    <div
      ref={setContainer}
      data-context-chat
      className="flex-1 overflow-hidden pr-0.5"
    >
      <List
        resolveEntityReferences
        messages={messages}
        height={containerHeight || 1}
        threadId={threadId}
        thread={thread}
        isCompact
      />
    </div>
  );
});
